import { useCallback } from 'react';
import * as Sentry from '@sentry/browser';
import { ACTIONS, useAppContext } from '../Contexts/AppContext';
import createFeedbackAPI from '@src/utils/FeedbackAPI';
import { API_ERROR_MESSAGE_KEYS_MAP } from '@src/utils/constants';

const useFeedbackApi = () => {
  const {
    dispatch,
    state: {
      editorType,
      feedbackCreatorUserGuid,
      feedbackId,
      metaSiteId,
      ownerId,
      pMetaSiteId,
      siteId,
      siteName,
      linkKey,
      instance,
    },
  } = useAppContext();

  const feedbackAPI = createFeedbackAPI({
    editorType,
    feedbackCreatorUserGuid,
    feedbackId,
    metaSiteId,
    ownerId,
    pMetaSiteId,
    siteId,
    siteName,
    linkKey,
    instance,
  });

  const getInitializeData = useCallback(async () => {
    try {
      return await feedbackAPI.getInitializeData();
    } catch (e) {
      Sentry.captureException(e);
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: {
          showError: true,
          errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.genericError,
        },
      });
      return undefined;
    }
  }, [dispatch, feedbackAPI]);

  const addComment = useCallback(
    async ({ message, commentLocation, status, shouldSchedule, userInfo }) => {
      try {
        return await feedbackAPI.addComment({
          message,
          commentLocation,
          status,
          shouldSchedule,
          userInfo,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.addComment,
          },
        });
        return undefined;
      }
    },
    [dispatch, feedbackAPI],
  );

  const createContact = useCallback(
    async ({ userName, userEmail }) => {
      try {
        return await feedbackAPI.createContact({ userName, userEmail });
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            userName,
          },
        });
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.createContact,
          },
        });
        return { contact: { contactId: null } };
      }
    },
    [dispatch, feedbackAPI],
  );

  const updateComment = useCallback(
    async ({
      id,
      message,
      commentLocation,
      commenterId,
      status,
      revision,
      fields,
    }: {
      id: string;
      message: string;
      commentLocation: any;
      commenterId: string;
      status: string;
      revision: string;
      fields: any;
    }) => {
      try {
        return await feedbackAPI.updateComment({
          id,
          message,
          commentLocation,
          commenterId,
          status,
          revision,
          fields,
          feedbackId,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.updateComment,
          },
        });
      }
    },
    [dispatch, feedbackAPI, feedbackId],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      try {
        await feedbackAPI.deleteComment(commentId);
        return true;
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.deleteComment,
          },
        });
      }

      return false;
    },
    [dispatch, feedbackAPI],
  );

  const replyToComment = useCallback(
    async ({
      id,
      revision,
      replyText,
      userInfo,
      commenterId,
    }: {
      id: string;
      revision: string;
      replyText: string;
      userInfo: any;
      commenterId: string;
      commentLocation: any;
      status: string;
    }) => {
      try {
        return await feedbackAPI.replyToComment({
          id,
          revision,
          replyText,
          userInfo,
          commenterId,
          isOwner: false,
        });
      } catch (e) {
        Sentry.captureException(e);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: API_ERROR_MESSAGE_KEYS_MAP.replyComment,
          },
        });
        return undefined;
      }
    },
    [dispatch, feedbackAPI],
  );

  return {
    addComment,
    createContact,
    updateComment,
    deleteComment,
    replyToComment,
    getInitializeData,
  };
};

export default useFeedbackApi;
